/**
 * Abstract base for all tool-execution errors.
 *
 * Subclass for every distinct failure mode — never instantiate this base
 * directly. The class hierarchy under `lib/tool/errors/` is the registry:
 * `ls lib/tool/errors/` enumerates every error the system can raise. Each
 * subclass declares its own `static readonly errorType = '<ClassName>' as const;`
 * (mandatory — minification mangles `error.name` / `error.constructor.name`),
 * which is the wire-format discriminator surfaced to the LLM as `errorType`.
 * There is no parallel string-enum to maintain; the invariant is enforced by
 * `error-type-discriminator.test.ts`.
 *
 * Three top-level branches map 1:1 to three LLM recovery strategies:
 * - {@link InvalidToolInputError} → retry with different arguments
 * - {@link DataShapeError} → inspect the data, then retry
 * - {@link EnvironmentError} → fatal, cannot be fixed by retrying
 */

import type { z } from 'zod';

import type { ToolOutputErrorSchema } from '../output-types.ts';
import { formatErrorText } from './format.ts';

/**
 * Canonical list of recovery strategy values. Single source of truth — the
 * {@link ErrorRecoveryStrategy} type and any Zod schema that validates the
 * wire format both derive from this tuple.
 */
export const ERROR_RECOVERY_STRATEGIES = [
  /** LLM gave bad arguments. Retry with corrected arguments. */
  'retry-with-different-input',
  /** Args were structurally valid but the data didn't match. Inspect the data, then retry. */
  'inspect-data-then-retry',
  /** Tool harness misconfiguration. Cannot be fixed by retrying — report to user. */
  'fatal',
] as const;

/** Recovery strategy hint surfaced to the LLM. */
export type ErrorRecoveryStrategy = (typeof ERROR_RECOVERY_STRATEGIES)[number];

/**
 * Structured wire shape sent to the LLM as a tool-execution failure.
 *
 * Inferred from {@link ToolOutputErrorSchema} (the canonical Zod source of
 * truth) so the runtime validator and the static type cannot drift.
 */
export type ToolErrorResponse = z.infer<typeof ToolOutputErrorSchema>;

/**
 * Keys excluded from the default {@link ToolExecutionError.details}
 * passthrough — reserved Error fields (`name`, `message`, `stack`, `cause`)
 * plus the recovery-strategy hint, which is surfaced as a sibling field on
 * {@link ToolErrorResponse} rather than nested under `details`.
 */
const RESERVED_DETAIL_KEYS = new Set(['name', 'message', 'stack', 'cause', 'recovery']);

export abstract class ToolExecutionError extends Error {
  /** Recovery strategy. Set on each top-level branch class. */
  abstract readonly recovery: ErrorRecoveryStrategy;

  /**
   * Wire-format discriminator for this error class. MUST be declared on
   * every concrete subclass as `static readonly errorType = '<ClassName>' as const;`
   * — the base reads it instead of `error.name` because production
   * minification mangles class names to short symbols. Convention: the
   * value matches the un-minified class name (e.g. 'DatasetNotFoundError')
   * so {@link humanizeErrorType} can keep stripping the 'Error' suffix.
   * Enforced at runtime by `error-type-discriminator.test.ts`.
   */
  static readonly errorType: string = 'ToolExecutionError';

  constructor(message: string) {
    super(message);
    // Restore prototype chain for `instanceof` across module boundaries —
    // required when subclassing built-in `Error` in TS-compiled output.
    Object.setPrototypeOf(this, new.target.prototype);
    // Mirror the `@toolError`-stamped static `errorType` discriminator onto
    // the standard `Error.name` instance field. `this.name` is what every
    // JS-native error consumer (DevTools, vitest's `.rejects.toThrowError`,
    // `err.toString()`) reads, and keeping the two in sync removes the trap
    // where `error.constructor.errorType === 'FooError'` but
    // `error.name === 'Error'`.
    this.name = (new.target as typeof ToolExecutionError).errorType;
  }

  /**
   * Actionable recovery hints for the LLM. Override per-class. Default empty.
   */
  suggestions(): readonly string[] {
    return [];
  }

  /**
   * Structured fields the UI / LLM can branch on without parsing the message.
   * Default mirrors the instance's enumerable own properties (excluding
   * reserved Error fields and `recovery`), which covers the common case where
   * details are just a passthrough of constructor `readonly` parameters.
   * Override per-class when computed fields, deliberate exclusions, renames,
   * or formatted values are needed.
   */
  details(): Readonly<Record<string, unknown>> {
    return Object.fromEntries(Object.entries(this).filter(([k]) => !RESERVED_DETAIL_KEYS.has(k)));
  }

  /** Convert to the wire shape consumed by the LLM and the chat runtime. */
  toStructuredResponse(): ToolErrorResponse {
    const ctor = this.constructor as typeof ToolExecutionError;
    return {
      success: false,
      errorType: ctor.errorType,
      error: this.message,
      suggestions: this.suggestions(),
      details: this.details(),
      recovery: this.recovery,
    };
  }

  /**
   * Render this error as the markdown text shown to the user / LLM. Subclasses
   * with richer rendering needs (e.g. a missing-columns table instead of a
   * bullet list) can override.
   *
   * Delegates to the standalone {@link formatErrorText} so that the wire-side
   * renderer (which only has a parsed JSON envelope, not the live instance)
   * and any throw-side caller both produce identical output.
   */
  format(): string {
    return formatErrorText(this.toStructuredResponse());
  }
}
